import type { ImageCrop } from './types.js';

/** Physical crop geometry shared by the editor and native preview/print.
 * Rotation fills the frame without introducing empty corners. The crop centre
 * is expressed in the original source, before rotation or mirroring.
 */
export function coverPhotoFrame(source: { w: number; h: number }, box: { w: number; h: number }, crop?: ImageCrop) {
  const rotation = crop?.rotation ?? 0;
  const radians = rotation * Math.PI / 180;
  const cos = Math.abs(Math.cos(radians)), sin = Math.abs(Math.sin(radians));
  const w = cos * box.w + sin * box.h, h = sin * box.w + cos * box.h;
  const free = crop?.free === true;
  const zoom = free ? freeZoom(crop?.zoom) : Math.max(1, Math.min(8, crop?.zoom ?? 1));
  const scale = Math.max(w / source.w, h / source.h) * zoom;
  const sw = w / scale, sh = h / scale;
  // A free (cut-out) window may leave the picture — outside it is transparent —
  // so only the clamped cover window is bounded to the source.
  const bound = (value: number, extent: number) => free ? freeCentre(value) : Math.max(extent / 2, Math.min(1 - extent / 2, value));
  const cx = bound(crop?.cx ?? .5, sw / source.w), cy = bound(crop?.cy ?? .5, sh / source.h);
  return {
    crop: { cx, cy, zoom, ...(rotation ? { rotation } : {}), ...(free ? { free: true, ...(crop?.keep ? { keep: crop.keep } : {}) } : {}) } as ImageCrop,
    scale, rotation,
    source: { x: cx * source.w - sw / 2, y: cy * source.h - sh / 2, w: sw, h: sh },
    destination: { x: (box.w - w) / 2, y: (box.h - h) / 2, w, h },
  };
}

/** Drag in display pixels -> source centre, inverse-rotated and unmirrored. */
export function panPhotoCrop(source: { w: number; h: number }, box: { w: number; h: number }, crop: ImageCrop, dx: number, dy: number, flipX = false, flipY = false): ImageCrop {
  const frame = coverPhotoFrame(source, box, crop);
  const angle = -frame.rotation * Math.PI / 180;
  dx *= flipX ? -1 : 1; dy *= flipY ? -1 : 1;
  const x = dx * Math.cos(angle) - dy * Math.sin(angle);
  const y = dx * Math.sin(angle) + dy * Math.cos(angle);
  return coverPhotoFrame(source, box, { ...frame.crop, cx: frame.crop.cx - x / frame.scale / source.w, cy: frame.crop.cy - y / frame.scale / source.h }).crop;
}

/**
 * Where the WHOLE picture sits in the box for a crop, as the renderer draws it:
 * a rectangle in box units, turned by `rotation` degrees about the box centre.
 *
 * A live stand-in (a plain `<img>` or CSS background moved while a finger is
 * down) positions the picture with this, so what the shopper moves is exactly
 * what the print renderer draws when the finger lifts. Its own approximation of
 * the window left out the zoom a straightened photo needs to cover the box: the
 * picture shrank while it moved and jumped back when it was let go.
 */
export function photoPlacement(source: { w: number; h: number }, box: { w: number; h: number }, crop?: ImageCrop) {
  const frame = coverPhotoFrame(source, box, crop);
  return {
    x: frame.destination.x - frame.source.x * frame.scale,
    y: frame.destination.y - frame.source.y * frame.scale,
    w: source.w * frame.scale,
    h: source.h * frame.scale,
    rotation: frame.rotation,
    scale: frame.scale,
    /** The crop as the renderer reads it: clamped, zoom bounded. */
    crop: frame.crop,
  };
}

/**
 * Zooms a crop about a point of the box (box units from its top-left corner),
 * keeping the part of the picture under that point where it is — the pinch
 * midpoint or the mouse pointer, the way every photo app zooms. `to` moves that
 * point at the same time (a two-finger pinch that also slides). The point is in
 * the element's own unmirrored frame; rotation of the photo is accounted for.
 */
export function zoomPhotoCropAt(
  source: { w: number; h: number },
  box: { w: number; h: number },
  crop: ImageCrop,
  zoom: number,
  at: { x: number; y: number },
  to: { x: number; y: number } = at,
): ImageCrop {
  const before = coverPhotoFrame(source, box, crop);
  const after = coverPhotoFrame(source, box, { ...before.crop, zoom });
  const angle = -before.rotation * Math.PI / 180;
  const cos = Math.cos(angle), sin = Math.sin(angle);
  // Box point -> source offset from the window centre (inverse rotation, scale).
  const unrotate = (p: { x: number; y: number }, scale: number) => {
    const qx = p.x - box.w / 2, qy = p.y - box.h / 2;
    return { x: (qx * cos - qy * sin) / scale, y: (qx * sin + qy * cos) / scale };
  };
  const held = unrotate(at, before.scale);
  const landed = unrotate(to, after.scale);
  const cx = before.crop.cx + (held.x - landed.x) / source.w;
  const cy = before.crop.cy + (held.y - landed.y) / source.h;
  return coverPhotoFrame(source, box, { ...after.crop, cx, cy }).crop;
}

/**
 * Turns a framing to `rotation` degrees without changing how big the picture
 * is drawn. `zoom` is relative to the cover fit of the TURNED box, which grows
 * with the angle, so keeping `zoom` as it was enlarged the picture on every
 * turn — about the box centre, which pushed a photo framed off-centre sideways
 * ("it zooms to the right") and grew a cut-out head for no reason at all.
 *
 * A cut-out (`free`) keeps its exact size. A covering photo keeps its size too
 * unless the turned box would show a corner of empty paper; then it grows just
 * enough to cover, and no more. Pass the framing the turning STARTED from, not
 * the last turned one: a turn and its undo then land back where they began,
 * instead of ratcheting the picture bigger with every forced cover.
 */
export function turnPhotoCrop(source: { w: number; h: number }, box: { w: number; h: number }, from: ImageCrop, rotation: number): ImageCrop {
  const start = coverPhotoFrame(source, box, from);
  const free = from.free === true;
  const base = coverPhotoFrame(source, box, { cx: 0.5, cy: 0.5, zoom: 1, ...(rotation ? { rotation } : {}), ...(free ? { free: true } : {}) }).scale;
  const { rotation: _old, ...rest } = start.crop;
  void _old;
  return coverPhotoFrame(source, box, { ...rest, zoom: start.scale / base, ...(rotation ? { rotation } : {}) }).crop;
}

/**
 * The colour effects CSS reproduces exactly, as a `filter` value, for a live
 * stand-in. CSS grayscale/sepia/invert/brightness/contrast are the very
 * matrices the renderer applies (Rec. 709 luma, the classic sepia matrix,
 * scale about 0, contrast about 0.5), in the order the layer lists them.
 * Effects CSS cannot express (threshold, a tint, pixel rewrites) are left to
 * the full render, which follows the moment the gesture ends.
 */
export function photoCssFilter(effects?: readonly { kind: string; amount?: number }[]): string | undefined {
  if (!effects?.length) return undefined;
  const parts: string[] = [];
  for (const effect of effects) {
    if (effect.kind === 'grayscale') parts.push('grayscale(1)');
    else if (effect.kind === 'sepia') parts.push('sepia(1)');
    else if (effect.kind === 'invert') parts.push('invert(1)');
    else if (effect.kind === 'brightness' && Number.isFinite(effect.amount)) parts.push(`brightness(${effect.amount})`);
    else if (effect.kind === 'contrast' && Number.isFinite(effect.amount)) parts.push(`contrast(${effect.amount})`);
  }
  return parts.length ? parts.join(' ') : undefined;
}

/** A crop selection in unmirrored source coordinates, independent of viewport pixels. */
export interface PhotoCropRect { x: number; y: number; w: number; h: number }
export function photoCropRect(source: { w: number; h: number }, box: { w: number; h: number }, crop?: ImageCrop): PhotoCropRect {
  const frame = coverPhotoFrame(source, box, crop);
  const w = box.w / frame.scale / source.w, h = box.h / frame.scale / source.h;
  return { x: frame.crop.cx - w / 2, y: frame.crop.cy - h / 2, w, h };
}

/** Convert a source rectangle into the canonical crop and a physical frame.
 * The new frame fits inside the previous frame, keeps its centre and rotation,
 * and is rendered by coverPhotoFrame in both the editor and the print worker.
 */
export function cropPhotoRect(source: { w: number; h: number }, rect: PhotoCropRect, bounds: { w: number; h: number }, rotation = 0, zoom = 1) {
  const width = source.w * rect.w, height = source.h * rect.h;
  const physicalScale = Math.min(bounds.w / width, bounds.h / height);
  const sizeMm = { w: width * physicalScale, h: height * physicalScale };
  const base = coverPhotoFrame(source, sizeMm, { cx: rect.x + rect.w / 2, cy: rect.y + rect.h / 2, zoom: 1, rotation });
  const crop = coverPhotoFrame(source, sizeMm, { ...base.crop, cx: rect.x + rect.w / 2, cy: rect.y + rect.h / 2, zoom: Math.max(1, physicalScale / base.scale) * zoom }).crop;
  return { sizeMm, crop };
}

/** How far a free (cut-out) crop may zoom out: the picture at a tenth of the cover fit. */
export const FREE_ZOOM_MIN = 0.1;
/** And in: the same ceiling the clamped cover window has always had. */
export const FREE_ZOOM_MAX = 8;
const freeZoom = (zoom?: number): number => Math.max(FREE_ZOOM_MIN, Math.min(FREE_ZOOM_MAX, Number.isFinite(zoom) ? zoom! : 1));
/** A free window's centre may leave the picture, but not wander off to infinity. */
const freeCentre = (value: number): number => Math.max(-2, Math.min(3, Number.isFinite(value) ? value : .5));

/**
 * The part of the source a cover-fitted photo shows — the one window every
 * renderer (print, storefront preview, the on-canvas editor) draws.
 *
 * `cover` crops the overflowing axis and centres the result; `crop` refines
 * it: zoom shrinks the window, cx/cy recentre it, and the window is clamped
 * so it never leaves the source. A `free` crop (a transparent cut-out) is
 * neither clamped nor limited to zoom ≥ 1 — its window may extend past the
 * picture, which `clipPhotoWindow` then trims to what exists.
 */
export function photoWindow(srcW: number, srcH: number, boxW: number, boxH: number, crop?: ImageCrop): { x: number; y: number; w: number; h: number } {
  const srcAspect = srcW / srcH;
  const boxAspect = boxW / boxH;
  let w: number;
  let h: number;
  if (srcAspect > boxAspect) {
    w = srcH * boxAspect;
    h = srcH;
  } else {
    w = srcW;
    h = srcW / boxAspect;
  }
  const free = crop?.free === true;
  const zoom = free ? freeZoom(crop?.zoom) : Math.max(1, crop?.zoom ?? 1);
  w /= zoom;
  h /= zoom;
  const cx = (free ? freeCentre(crop?.cx ?? 0.5) : (crop?.cx ?? 0.5)) * srcW;
  const cy = (free ? freeCentre(crop?.cy ?? 0.5) : (crop?.cy ?? 0.5)) * srcH;
  if (free) return { x: cx - w / 2, y: cy - h / 2, w, h };
  const x = Math.min(Math.max(cx - w / 2, 0), srcW - w);
  const y = Math.min(Math.max(cy - h / 2, 0), srcH - h);
  return { x, y, w, h };
}

/**
 * Trims a source→destination draw to the pixels that exist.
 *
 * A free window may reach past the picture; the part outside is transparent,
 * so the draw shrinks to the overlap and the destination shrinks in the same
 * proportion. Returns undefined when nothing of the picture is in view. A
 * window already inside the source comes back unchanged.
 */
export function clipPhotoWindow(
  src: { x: number; y: number; w: number; h: number },
  dst: { x: number; y: number; w: number; h: number },
  srcW: number,
  srcH: number,
  keep?: { x: number; y: number; w: number; h: number },
): { src: { x: number; y: number; w: number; h: number }; dst: { x: number; y: number; w: number; h: number } } | undefined {
  // What may be drawn: the picture, or the kept part of it (in source pixels).
  const k = keep && keep.w > 0 && keep.h > 0
    ? { x0: Math.max(0, keep.x * srcW), y0: Math.max(0, keep.y * srcH), x1: Math.min(srcW, (keep.x + keep.w) * srcW), y1: Math.min(srcH, (keep.y + keep.h) * srcH) }
    : { x0: 0, y0: 0, x1: srcW, y1: srcH };
  if (src.x >= k.x0 && src.y >= k.y0 && src.x + src.w <= k.x1 && src.y + src.h <= k.y1) return { src, dst };
  const x0 = Math.max(src.x, k.x0), y0 = Math.max(src.y, k.y0);
  const x1 = Math.min(src.x + src.w, k.x1), y1 = Math.min(src.y + src.h, k.y1);
  if (x1 <= x0 || y1 <= y0 || src.w <= 0 || src.h <= 0) return undefined;
  const kx = dst.w / src.w, ky = dst.h / src.h;
  return {
    src: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
    dst: { x: dst.x + (x0 - src.x) * kx, y: dst.y + (y0 - src.y) * ky, w: (x1 - x0) * kx, h: (y1 - y0) * ky },
  };
}
